import { DeadlineDateType, PaymentMethod, PaymentRequestItemType, PaymentRequestStatus, RuleType, } from '@src/types';
import { round as _round, isEmpty, omit } from 'lodash';
import { getLocalDate } from 'pe-core-frontend';
import { isFutureDateInTimezone } from './dateHelper';
/**
 * Checks if a date string is valid (not empty, not "Invalid Date", and parseable as ISO date).
 * Returns undefined for invalid dates, otherwise returns the original string.
 */
const cleanDateString = (dateStr) => {
    if (!dateStr || dateStr === 'Invalid Date') {
        return undefined;
    }
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateStr)) {
        return undefined;
    }
    return dateStr;
};
/**
 * Sanitizes a PaymentRequest for use in benefits/lookup API calls.
 * Removes non-serializable fields and cleans date strings.
 */
export const sanitizePaymentRequestForSpec = (paymentRequest) => {
    return {
        ...omit(paymentRequest, '__typename', 'createdAt', 'updatedAt', 'program', 'client', 'processedBy', 'processedById', 'submittedAt', 'amountToPay', 'paymentRequestItems', 'documents', 'capYear', 'capSegment', 'datesSameAsProgram'),
        amountRequested: paymentRequest.amountRequested ?? 0,
        termStartDate: cleanDateString(paymentRequest.termStartDate),
        termEndDate: cleanDateString(paymentRequest.termEndDate),
    };
};
export const validatePaymentRequest = (paymentRequest, t, getPolicy, program, formatCurrency) => {
    const errors = {};
    if (!paymentRequest.paymentMethod) {
        errors.paymentMethod = t('required');
    }
    if (!paymentRequest.termName || paymentRequest.termName.trim() === '') {
        errors.termName = t('required');
    }
    if (!paymentRequest.termStartDate) {
        errors.termStartDate = t('required');
    }
    if (!paymentRequest.termEndDate) {
        errors.termEndDate = t('required');
    }
    if (paymentRequest.termStartDate && paymentRequest.termEndDate) {
        if (new Date(paymentRequest.termStartDate) > new Date(paymentRequest.termEndDate)) {
            errors.termEndDate = t('End date must be after start date');
        }
    }
    // Validate amountRequested when user hasn't paid yet AND budget policy is enabled
    if (!paymentRequest.requiresExpenses && getPolicy && program) {
        const budgetPolicy = getPolicy(RuleType.BUDGET, program);
        if (budgetPolicy && budgetPolicy.approvalMode) {
            if (!paymentRequest.amountRequested || Number(paymentRequest.amountRequested) === 0) {
                errors.amountRequested = t('required');
            }
            else if (Number(paymentRequest.amountRequested) < 0) {
                errors.amountRequested = t('must be greater than 0');
            }
            else if (isNaN(Number(paymentRequest.amountRequested))) {
                errors.amountRequested = t('must be a decimal number');
            }
            else {
                // Check funding limit from funding policy
                const funding = getPolicy(RuleType.FUNDING, program);
                // Skip validation if funding is unlimited
                if (funding !== undefined &&
                    !funding.isUnlimited &&
                    paymentRequest.amountRequested &&
                    Number(paymentRequest.amountRequested) > Number(funding.remaining)) {
                    const formattedAmount = formatCurrency
                        ? formatCurrency(Number(funding.remaining), funding.currency, true)
                        : `$${Number(funding.remaining).toFixed(2)}`;
                    errors.amountRequested = t('You can request up to {remaining} in total costs for this term, based on your previous activity and approved funding for this benefit period.', {
                        values: {
                            remaining: formattedAmount,
                        },
                    });
                }
            }
        }
    }
    return errors;
};
export const validatePaymentRequestItem = (paymentRequestItem, t, requireFeeType, clientTimezone) => {
    const errors = {};
    if (!paymentRequestItem)
        return errors;
    if (!paymentRequestItem.amountRequested || paymentRequestItem.amountRequested === 0) {
        errors.amountRequested = t('required');
    }
    if (paymentRequestItem.amountRequested && paymentRequestItem.amountRequested < 0) {
        errors.amountRequested = t('must be greater than 0');
    }
    // check if its a number
    if (paymentRequestItem.amountRequested && isNaN(paymentRequestItem.amountRequested)) {
        errors.amountRequested = t('must be a decimal number');
    }
    if (!paymentRequestItem.type) {
        errors.type = t('required');
    }
    if (!paymentRequestItem.title) {
        errors.title = t('required');
    }
    if (paymentRequestItem.type === PaymentRequestItemType.COURSE) {
        if (!paymentRequestItem.courseCode) {
            errors.courseCode = t('required');
        }
    }
    if (paymentRequestItem.type === PaymentRequestItemType.FEE) {
        if (requireFeeType && !paymentRequestItem.feeTypeId) {
            errors.feeTypeId = t('required');
        }
    }
    // Validate receipt date
    // Receipt date is required when currency conversion is being used (indicated by originalAmountRequestedCurrency being set)
    const hasCurrencyConversion = paymentRequestItem.originalAmountRequestedCurrency &&
        paymentRequestItem.originalAmountRequestedCurrency !== '';
    if (hasCurrencyConversion && !paymentRequestItem.receiptDate) {
        errors.receiptDate = t('required');
    }
    else if (paymentRequestItem.receiptDate) {
        const receiptDate = new Date(paymentRequestItem.receiptDate);
        // Check if date is valid
        if (isNaN(receiptDate.getTime()) || paymentRequestItem.receiptDate === 'Invalid Date') {
            errors.receiptDate = t('Please enter a valid date');
        }
        else {
            // Check if date is in the future, taking into account client timezone
            const timezone = clientTimezone || 'UTC';
            if (isFutureDateInTimezone(paymentRequestItem.receiptDate, timezone)) {
                errors.receiptDate = t('must be in the past');
            }
        }
    }
    return errors;
};
export const isCourseItem = (item) => {
    return item.type === PaymentRequestItemType.COURSE;
};
export const isFeeItem = (item) => {
    return item.type === PaymentRequestItemType.FEE;
};
export const isBookMaterialItem = (item) => {
    return item.type === PaymentRequestItemType.BOOK || item.type === PaymentRequestItemType.MATERIAL;
};
export const isScholarshipItem = (item) => {
    return item.type === PaymentRequestItemType.SCHOLARSHIP;
};
export const getTotalRequested = (paymentRequest, deductScholarships = false) => {
    let total = getActiveItems(paymentRequest)
        .filter(item => deductScholarships || !isScholarshipItem(item))
        .reduce((total, item) => total + (isScholarshipItem(item) ? -1 : 1) * Number(item.amountRequested || 0), 0);
    total = Math.max(total, 0);
    return total;
};
export const getTotalApproved = (paymentRequest) => {
    let total = getActiveItems(paymentRequest)
        .filter(item => !isScholarshipItem(item))
        .reduce((total, item) => total + Number(item.amountApproved || 0), 0);
    total = Math.max(total, 0);
    return total;
};
export const getPreimbursementRequested = (paymentRequest, academicPolicy) => {
    let total = getTotalRequested(paymentRequest);
    if (academicPolicy) {
        if (academicPolicy.preimbursementPayoutPct) {
            total = (total * academicPolicy.preimbursementPayoutPct) / 100;
        }
        if (academicPolicy.preimbursementPayoutLimit) {
            total = Math.min(total, academicPolicy.preimbursementPayoutLimit);
        }
    }
    return total;
};
export const showItemDetails = (paymentRequest) => {
    return [
        PaymentRequestStatus.READY_FOR_PAYMENT,
        PaymentRequestStatus.REJECTED,
        PaymentRequestStatus.CLOSED,
        PaymentRequestStatus.APPROVED,
    ].includes(paymentRequest.status);
};
/**
 * Enum representing different types of adjustments.
 * @enum {string}
 * @readonly
 */
var Adjustments;
(function (Adjustments) {
    /**
     * Scholarship amount applied
     */
    Adjustments["SCHOLARSHIP"] = "SCHOLARSHIP";
    /**
     * Previously paid amount
     */
    Adjustments["PREVIOUSLY_PAID"] = "PREVIOUSLY_PAID";
    /**
     * Percentage payout based on grade
     */
    Adjustments["GRADE_BASED_PAYOUT"] = "GRADE_BASED_PAYOUT";
    /**
     * Percentage payout for preimbursement
     */
    Adjustments["PREIMBURSEMENT_PAYOUT"] = "PREIMBURSEMENT_PAYOUT";
    /**
     * Limit payout for preimbursement
     */
    Adjustments["PREIMBURSEMENT_LIMIT"] = "PREIMBURSEMENT_LIMIT";
    /**
     * Funding applied to the item
     */
    Adjustments["FUNDING_APPLIED"] = "FUNDING_APPLIED";
    /**
     * Earmark/budget limit adjustment
     */
    Adjustments["EARMARK_LIMIT"] = "EARMARK_LIMIT";
})(Adjustments || (Adjustments = {}));
export const round = (value, decimals = 2) => {
    return _round(value, decimals);
};
/**
 * Retrieves the payment request item adjustments based on the provided item.
 * @param item - The payment request item.
 * @returns The adjustment object containing the calculated adjustments.
 */
export const getPaymentRequestItemAdjustments = (item) => {
    const adjustment = {
        gradePayoutPercent: 100,
        preimbursementPayoutPercent: 100,
        preimbursementLimit: undefined,
        SCHOLARSHIP: {
            deduction: 0,
            result: 0,
        },
        PREVIOUSLY_PAID: {
            deduction: 0,
            result: 0,
        },
        GRADE_BASED_PAYOUT: {
            deduction: 0,
            result: 0,
        },
        PREIMBURSEMENT_PAYOUT: {
            deduction: 0,
            result: 0,
        },
        PREIMBURSEMENT_LIMIT: {
            deduction: 0,
            result: 0,
        },
        FUNDING_APPLIED: {
            deduction: 0,
            result: 0,
        },
        EARMARK_LIMIT: {
            deduction: 0,
            result: 0,
        },
        policyType: item.calculations?.policy_type,
    };
    if (isEmpty(item.calculations))
        return adjustment;
    const scholarshipUsed = round(Number(item.calculations.start_scholarship_remaining || 0) -
        Number(item.calculations.end_scholarship_remaining || 0));
    adjustment.SCHOLARSHIP = {
        deduction: scholarshipUsed,
        result: round(Math.max(0, (item.amountApproved ?? 0) - scholarshipUsed)),
    };
    const previouslyPaid = round(Number(item.calculations.previously_paid || 0));
    adjustment.PREVIOUSLY_PAID = {
        deduction: previouslyPaid,
        result: round(Math.max(0, adjustment.SCHOLARSHIP.result - previouslyPaid)),
    };
    adjustment.gradePayoutPercent = item.calculations.grade_payout_pct ?? 100;
    const gradeReduction = round((adjustment.PREVIOUSLY_PAID.result * (100 - adjustment.gradePayoutPercent)) / 100);
    adjustment.GRADE_BASED_PAYOUT = {
        deduction: gradeReduction,
        result: round(Math.max(0, adjustment.PREVIOUSLY_PAID.result - gradeReduction)),
    };
    adjustment.preimbursementPayoutPercent = item.calculations.preimbursement_payout_pct ?? 100;
    const preimbursementReduction = round((adjustment.PREVIOUSLY_PAID.result * (100 - adjustment.preimbursementPayoutPercent)) / 100);
    adjustment.PREIMBURSEMENT_PAYOUT = {
        deduction: preimbursementReduction,
        result: round(Math.max(0, adjustment.PREVIOUSLY_PAID.result - preimbursementReduction)),
    };
    adjustment.preimbursementLimit = item.calculations.preimbursement_payout_limit;
    if (adjustment.preimbursementLimit) {
        const preimbursementLimitReduction = round(Math.max(0, adjustment.PREIMBURSEMENT_PAYOUT.result - adjustment.preimbursementLimit));
        adjustment.PREIMBURSEMENT_LIMIT = {
            deduction: preimbursementLimitReduction,
            result: round(Math.max(0, adjustment.PREIMBURSEMENT_PAYOUT.result - preimbursementLimitReduction)),
        };
    }
    const fundingApplied = round(Number(item.calculations.start_program_cap_remaining || 0) -
        Number(item.calculations.end_program_cap_remaining || 0));
    adjustment.FUNDING_APPLIED = {
        deduction: fundingApplied,
        result: fundingApplied,
    };
    const earmarkAdjustment = round(Number(item.calculations.earmark_adjustment || 0));
    adjustment.EARMARK_LIMIT = {
        deduction: earmarkAdjustment,
        result: round(Math.max(0, adjustment.FUNDING_APPLIED.result - earmarkAdjustment)),
    };
    return adjustment;
};
export const getPaymentRequestAdjustments = (paymentRequest) => {
    const adjustment = {
        gradePayoutPercent: 100,
        preimbursementPayoutPercent: 100,
        preimbursementLimit: undefined,
        SCHOLARSHIP: {
            deduction: 0,
            result: 0,
        },
        PREVIOUSLY_PAID: {
            deduction: 0,
            result: 0,
        },
        GRADE_BASED_PAYOUT: {
            deduction: 0,
            result: 0,
        },
        PREIMBURSEMENT_PAYOUT: {
            deduction: 0,
            result: 0,
        },
        PREIMBURSEMENT_LIMIT: {
            deduction: 0,
            result: 0,
        },
        FUNDING_APPLIED: {
            deduction: 0,
            result: 0,
        },
        EARMARK_LIMIT: {
            deduction: 0,
            result: 0,
        },
    };
    for (const item of getActiveItems(paymentRequest)) {
        if (item.type === PaymentRequestItemType.SCHOLARSHIP) {
            continue;
        }
        const itemAdjustments = getPaymentRequestItemAdjustments(item);
        adjustment.SCHOLARSHIP.deduction += itemAdjustments.SCHOLARSHIP.deduction;
        adjustment.SCHOLARSHIP.result += itemAdjustments.SCHOLARSHIP.result;
        adjustment.PREVIOUSLY_PAID.deduction += itemAdjustments.PREVIOUSLY_PAID.deduction;
        adjustment.PREVIOUSLY_PAID.result += itemAdjustments.PREVIOUSLY_PAID.result;
        adjustment.GRADE_BASED_PAYOUT.deduction += itemAdjustments.GRADE_BASED_PAYOUT.deduction;
        adjustment.GRADE_BASED_PAYOUT.result += itemAdjustments.GRADE_BASED_PAYOUT.result;
        adjustment.PREIMBURSEMENT_PAYOUT.deduction += itemAdjustments.PREIMBURSEMENT_PAYOUT.deduction;
        adjustment.PREIMBURSEMENT_PAYOUT.result += itemAdjustments.PREIMBURSEMENT_PAYOUT.result;
        adjustment.PREIMBURSEMENT_LIMIT.deduction += itemAdjustments.PREIMBURSEMENT_LIMIT.deduction;
        adjustment.PREIMBURSEMENT_LIMIT.result += itemAdjustments.PREIMBURSEMENT_LIMIT.result;
        adjustment.FUNDING_APPLIED.deduction += itemAdjustments.FUNDING_APPLIED.deduction;
        adjustment.FUNDING_APPLIED.result += itemAdjustments.FUNDING_APPLIED.result;
        adjustment.EARMARK_LIMIT.deduction += itemAdjustments.EARMARK_LIMIT.deduction;
        adjustment.EARMARK_LIMIT.result += itemAdjustments.EARMARK_LIMIT.result;
    }
    return adjustment;
};
export const getActiveItems = (paymentRequest) => {
    return paymentRequest.paymentRequestItems.filter(item => !item.isDeleted);
};
export const getIntervalDays = (interval) => {
    // Determine direction based on whether the interval is backward or not
    const direction = interval.backward ? -1 : 1;
    // Calculate the total number of days to shift, including years converted to days
    const totalDays = (interval.days ?? 0 + (interval.years ?? 0) * 365) * direction;
    return totalDays;
};
export const calculateIntervalDate = (item, dateType, interval) => {
    const totalDays = getIntervalDays(interval);
    // Choose the date to check based on the dateType
    const baseDate = dateType.toLowerCase() === DeadlineDateType.START_DATE ? item.termStartDate : item.termEndDate;
    // Return early if baseDate is not set
    if (!baseDate) {
        return;
    }
    // Create a new Date object based on the baseDate
    const resultDate = getLocalDate(baseDate);
    if (!resultDate) {
        return;
    }
    // Add the calculated days to the resultDate
    resultDate.setDate(resultDate.getDate() + totalDays);
    return resultDate;
};
export const isUploadDocumentRequired = (item, policy, t) => {
    if (policy) {
        const approvalPolicyConfig = item.paymentMethod === PaymentMethod.PREIMBURSEMENT
            ? policy.preimbursementApproval
            : policy.paymentRequestApproval;
        if (approvalPolicyConfig) {
            const { success: withinDocumentDeadline } = isWithinDocumentDeadline(item, policy, t);
            return withinDocumentDeadline || approvalPolicyConfig.optionalDocument !== true;
        }
    }
    return true;
};
export const isWithinDocumentDeadline = (item, policy, t) => {
    const approvalPolicyConfig = item.paymentMethod === PaymentMethod.PREIMBURSEMENT
        ? policy?.preimbursementApproval
        : policy?.paymentRequestApproval;
    const documentDeadline = approvalPolicyConfig?.documentDeadline;
    if (!approvalPolicyConfig?.enableDocumentDeadline || !documentDeadline) {
        return { success: true };
    }
    let fromDate;
    let isValid = true;
    if (documentDeadline.minInterval &&
        Number(documentDeadline.minInterval?.days ?? 0) >= 0 &&
        documentDeadline.minIntervalBasedOn) {
        fromDate = calculateIntervalDate(item, documentDeadline.minIntervalBasedOn, documentDeadline.minInterval);
        if (fromDate && new Date() < fromDate) {
            isValid = false;
        }
    }
    let toDate;
    if (documentDeadline.maxInterval &&
        Number(documentDeadline.maxInterval?.days ?? 0) >= 0 &&
        documentDeadline.maxIntervalBasedOn) {
        toDate = calculateIntervalDate(item, documentDeadline.maxIntervalBasedOn, documentDeadline.maxInterval);
        if (toDate && new Date() > toDate) {
            isValid = false;
        }
    }
    if (isValid) {
        return { success: true };
    }
    if (fromDate && toDate) {
        return {
            success: false,
            message: t('Document submission for term cannot be made before {from_date} or after {to_date}', {
                values: {
                    from_date: fromDate,
                    to_date: toDate,
                },
            }),
        };
    }
    else if (fromDate) {
        return {
            success: false,
            message: t('Document submission for term cannot be made before {from_date}', {
                values: {
                    from_date: fromDate,
                },
            }),
        };
    }
    else {
        return {
            success: false,
            message: t('Document submission for term cannot be made after {to_date}', {
                values: {
                    to_date: toDate,
                },
            }),
        };
    }
};
/**
 * Helper function to check if current date is within the deadline window
 * Returns true if the current date is within the document deadline range
 */
export const isWithinDeadline = (item, policy) => {
    const approvalPolicyConfig = item.paymentMethod === PaymentMethod.PREIMBURSEMENT
        ? policy?.preimbursementApproval
        : policy?.paymentRequestApproval;
    // Check if deadline is enabled
    if (!approvalPolicyConfig?.enableDeadline) {
        return false;
    }
    const documentDeadline = approvalPolicyConfig?.documentDeadline;
    if (!documentDeadline) {
        return false;
    }
    const now = new Date();
    // Calculate from date (min interval)
    let fromDate;
    if (documentDeadline.minInterval &&
        Number(documentDeadline.minInterval?.days ?? 0) >= 0 &&
        documentDeadline.minIntervalBasedOn) {
        fromDate = calculateIntervalDate(item, documentDeadline.minIntervalBasedOn, documentDeadline.minInterval);
        if (fromDate && now < fromDate) {
            return false;
        }
    }
    // Calculate to date (max interval)
    let toDate;
    if (documentDeadline.maxInterval &&
        Number(documentDeadline.maxInterval?.days ?? 0) >= 0 &&
        documentDeadline.maxIntervalBasedOn) {
        toDate = calculateIntervalDate(item, documentDeadline.maxIntervalBasedOn, documentDeadline.maxInterval);
        if (toDate && now > toDate) {
            return false;
        }
    }
    return true;
};
/**
 * Check eligibility lock for payment request
 * Can submit expenses if:
 * - Eligibility lock is not enabled
 * - Eligibility lock is enabled and the from document deadline has past
 * - Payment Request is a preimbursement
 * - Workflow is enabled (workflow handles approvals separately)
 *
 * Expenses are required:
 * - if can_submit_expenses is True AND
 * - requires_expenses field is True/None OR past max deadline
 */
export const checkEligibilityLock = (paymentRequest, _program, eligibilityPolicy, approvalPolicy) => {
    /**
     * Helper to determine if expenses are required now.
     * Matches backend logic in backend/crud/payment_request.py#calculate_expenses_required
     */
    const calculateExpensesRequired = (canSubmit) => {
        if (!canSubmit) {
            return false;
        }
        // If user explicitly chose not to submit expenses yet
        if (paymentRequest.requiresExpenses === false) {
            // Check if past max deadline - if so, require despite user preference
            if (approvalPolicy?.paymentRequestApproval &&
                approvalPolicy.paymentRequestApproval.enableDocumentDeadline) {
                const deadlineCfg = approvalPolicy.paymentRequestApproval.documentDeadline;
                if (deadlineCfg &&
                    deadlineCfg.maxInterval &&
                    deadlineCfg.maxInterval.days != null &&
                    deadlineCfg.maxIntervalBasedOn &&
                    [DeadlineDateType.START_DATE, DeadlineDateType.END_DATE].includes(deadlineCfg.maxIntervalBasedOn)) {
                    const toDate = calculateIntervalDate(paymentRequest, deadlineCfg.maxIntervalBasedOn, deadlineCfg.maxInterval);
                    if (toDate && new Date() > toDate) {
                        return true; // Past deadline - require expenses
                    }
                }
            }
            // Within deadline window, expenses optional
            return false;
        }
        // requires_expenses is True or None (default) - require expenses
        return true;
    };
    const result = {
        locked: false,
        canSubmitExpenses: true,
        expensesRequired: calculateExpensesRequired(true),
        preimbursement: eligibilityPolicy?.enablePreimbursement ?? false,
    };
    if (!eligibilityPolicy ||
        !approvalPolicy ||
        !approvalPolicy.paymentRequestApproval ||
        paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT ||
        approvalPolicy.paymentRequestApproval.enableWorkflow) {
        return result;
    }
    if (!eligibilityPolicy.enableEligibilityLock) {
        return result;
    }
    result.locked = true;
    const deadline = approvalPolicy.paymentRequestApproval.enableDocumentDeadline &&
        approvalPolicy.paymentRequestApproval.documentDeadline;
    if (!deadline) {
        result.expensesRequired = calculateExpensesRequired(true);
        return result;
    }
    if (!deadline.minInterval || !deadline.minIntervalBasedOn) {
        // no FROM deadline set, nothing to check
        result.expensesRequired = calculateExpensesRequired(true);
        return result;
    }
    if (!paymentRequest.termStartDate || !paymentRequest.termEndDate) {
        // make sure the term dates are set
        return {
            ...result,
            canSubmitExpenses: false,
            expensesRequired: calculateExpensesRequired(false),
        };
    }
    const fromDate = calculateIntervalDate(paymentRequest, deadline.minIntervalBasedOn, deadline.minInterval);
    if (fromDate && new Date() < fromDate) {
        // before FROM deadline
        return {
            ...result,
            canSubmitExpenses: false,
            expensesRequired: calculateExpensesRequired(false),
        };
    }
    result.expensesRequired = calculateExpensesRequired(true);
    return result;
};
export const getTermDate = (paymentRequest, dateType, interval) => {
    if (!dateType || !interval)
        return;
    return calculateIntervalDate(paymentRequest, dateType, interval);
};
/**
 * Get the deadline error message based on the provided parameters.
 *
 * Keep this function in sync with the backend implementation at backend/managers/payment_request_manager.py#_get_deadline_error_message
 * @param formatDate - The function to format the date.
 * @param expired - Whether the deadline has expired.
 * @param deadline - The approval deadline.
 * @param fromDate - The start date of the term.
 * @param toDate - The end date of the term.
 * @param includeSpecificDates - Whether to include the specific dates in the message.
 * @returns - The error message and the parameters for the message.
 */
export const getDeadlineErrorMessage = (formatDate, expired, deadline, fromDate, toDate, includeSpecificDates = false) => {
    let msgText = 'You can submit your reimbursable expenses ';
    const minDays = fromDate && deadline.minInterval ? Math.abs(getIntervalDays(deadline.minInterval)) : 0;
    const maxDays = toDate && deadline.maxInterval ? Math.abs(getIntervalDays(deadline.maxInterval)) : 0;
    let minDirection = '';
    let minDateType = '';
    let maxDirection = '';
    let maxDateType = '';
    if (fromDate) {
        if (minDays === 0) {
            msgText += 'on the {min_date_type} ';
            minDateType = `term ${deadline.minIntervalBasedOn?.toLowerCase() === DeadlineDateType.START_DATE
                ? 'start date'
                : 'end date'}`;
        }
        else {
            msgText += 'as soon as {min_days} day(s) {min_direction} the {min_date_type} ';
            minDirection = deadline.minInterval?.backward ? 'before' : 'after';
            minDateType = `term ${deadline.minIntervalBasedOn?.toLowerCase() === DeadlineDateType.START_DATE
                ? 'starts'
                : 'ends'}`;
        }
    }
    if (toDate) {
        if (fromDate) {
            msgText += 'and ';
        }
        if (maxDays === 0) {
            msgText += 'no later than the {max_date_type} ';
            maxDateType = `term ${deadline.maxIntervalBasedOn?.toLowerCase() === DeadlineDateType.START_DATE
                ? 'start date'
                : 'end date'}`;
        }
        else {
            msgText += 'no later than {max_days} days {max_direction} the {max_date_type} ';
            maxDirection = deadline.maxInterval?.backward ? 'before' : 'after';
            maxDateType = `term ${deadline.maxIntervalBasedOn?.toLowerCase() === DeadlineDateType.START_DATE
                ? 'starts'
                : 'ends'}`;
        }
    }
    if (includeSpecificDates && fromDate && toDate) {
        msgText =
            msgText.trim() +
                (expired
                    ? '. Expenses for this request needed to be submitted by {to_date} '
                    : '. For this request, submissions open on {from_date} and close on {to_date} ');
    }
    msgText = `${msgText.trim()}.`;
    const params = {
        min_days: minDays,
        max_days: maxDays,
        from_date: formatDate(fromDate ? fromDate.toISOString() : undefined, { dateStyle: 'medium' }),
        to_date: formatDate(toDate ? toDate.toISOString() : undefined, { dateStyle: 'medium' }),
        min_direction: minDirection,
        min_date_type: minDateType,
        max_direction: maxDirection,
        max_date_type: maxDateType,
        title: expired
            ? 'You can no longer request reimbursements for this expense.'
            : 'Come back soon!',
    };
    return [msgText, params];
};
export const getDeadlinePolicyMessage = (deadline) => {
    // No deadline
    if (!deadline) {
        return ['When available', {}];
    }
    else {
        const minDays = deadline.minInterval?.days ?? 0;
        const minDirection = deadline.minInterval?.backward ? 'before' : 'after';
        const minDateType = deadline.minIntervalBasedOn?.toLowerCase() === DeadlineDateType.START_DATE
            ? 'starts'
            : 'ends';
        const maxDays = deadline.maxInterval?.days ?? 0;
        const maxDirection = deadline.maxInterval?.backward ? 'before' : 'after';
        const maxDateType = deadline.maxIntervalBasedOn?.toLowerCase() === DeadlineDateType.START_DATE
            ? 'starts'
            : 'ends';
        const params = {
            min_days: minDays,
            max_days: maxDays,
            min_direction: minDirection,
            min_date_type: minDateType,
            max_direction: maxDirection,
            max_date_type: maxDateType,
        };
        let msgText = 'As soon as ';
        if (minDays === 0) {
            msgText += 'the term ';
        }
        else {
            msgText += '{min_days} days {min_direction} the term ';
        }
        msgText += '{min_date_type} and no later than ';
        if (maxDays === 0) {
            msgText += 'when the term ';
        }
        else {
            msgText += '{max_days} days {max_direction} the term ';
        }
        msgText += '{max_date_type}';
        return [msgText, params];
    }
};
export const fillPlaceholders = (content, paymentRequest, dtFmt) => {
    const termDates = `${dtFmt(paymentRequest.termStartDate, { dateStyle: 'medium' })} - ${dtFmt(paymentRequest.termEndDate, { dateStyle: 'medium' })}`;
    return content.replace('{term}', termDates);
};
